/**
 * Phase 12 small-update: Pinned Notes Sidebar.
 *
 * Floating button in the bottom-right that pops out a slide-over showing
 * pinned notes. Click any pinned note → opens it in the same panel for
 * quick editing without leaving the current page.
 */
import React, { useEffect, useMemo, useState } from 'react';
import api from '../lib/api';
import { Pin, X, ChevronLeft } from 'lucide-react';
import { cstDateTime } from '../lib/time';
import { toast } from 'sonner';

export default function PinnedNotesSidebar() {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState([]);
  const [active, setActive] = useState(null);     // id of currently-viewed note
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [dirty, setDirty] = useState(false);

  // Phase 16 §5: respond to QuickTab event — hide stand-alone trigger.
  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener('atde:open-notes', onOpen);
    return () => window.removeEventListener('atde:open-notes', onOpen);
  }, []);

  const load = async () => {
    try {
      const { data } = await api.get(`/notes?_t=${Date.now()}`);
      setNotes((data.notes || []).filter((n) => n.pinned));
    } catch {}
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 30000);   // light refresh, won't fight the Notes page
    return () => clearInterval(iv);
  }, []);

  // When a pinned note is opened, hydrate the editor.
  const openNote = (n) => {
    setActive(n.id);
    setTitle(n.title);
    setBody(n.body);
    setDirty(false);
  };

  const save = async (opts = {}) => {
    if (!active) return;
    try {
      await api.put(`/notes/${active}`, { title, body });
      setDirty(false);
      if (!opts.silent) {
        await load();
        toast.success('Saved');
      } else {
        load(); // fire-and-forget refresh
      }
    } catch { if (!opts.silent) toast.error('Save failed'); }
  };

  // Autosave when the user exits the editor or closes the panel while
  // holding unsaved edits. Prevents "wrote a note, closed panel, lost it".
  const close = async () => {
    if (dirty && active) {
      await save({ silent: true });
    }
    setOpen(false);
    setActive(null);
  };

  const backToList = async () => {
    if (dirty && active) {
      await save({ silent: true });
    }
    setActive(null);
  };

  const activeNote = useMemo(() => notes.find((n) => n.id === active), [notes, active]);

  return (
    <>
      {/* Phase 16 §5: stand-alone trigger removed — opened via QuickTab. */}

      {/* SLIDE-OVER */}
      <aside
        data-testid="pinned-notes-sidebar"
        className={`fixed z-50 top-0 right-0 h-full w-[360px] bg-black border-l border-cyan-sys
                    transition-transform duration-300 ease-out flex flex-col
                    ${open ? 'translate-x-0' : 'translate-x-full'}`}
      >
        {/* HEADER */}
        <div className="flex items-center justify-between px-3 py-2 border-b border-[var(--border-default)]">
          <div className="flex items-center gap-2">
            {active && (
              <button
                data-testid="pinned-notes-back"
                onClick={backToList}
                className="text-[var(--txt-secondary)] hover:text-cyan-sys p-1"
                title="Back to list"
              >
                <ChevronLeft size={14} />
              </button>
            )}
            <Pin size={12} className="text-hold" />
            <span className="text-[11px] tracking-[0.3em] uppercase text-cyan-sys">
              {active ? 'pinned · editor' : `pinned notes · ${notes.length}`}
            </span>
          </div>
          <button
            data-testid="pinned-notes-close"
            onClick={close}
            className="text-[var(--txt-secondary)] hover:text-sell p-1"
            title="Close"
          >
            <X size={14} />
          </button>
        </div>

        {/* CONTENT */}
        <div className="flex-1 overflow-y-auto">
          {!active ? (
            <div className="p-2 space-y-1">
              {notes.length === 0 && (
                <div className="text-[var(--txt-muted)] text-[11px] text-center py-8 font-mono">
                  // no pinned notes<br />
                  pin a note from the Notes tab to access it here
                </div>
              )}
              {notes.map((n) => (
                <button
                  key={n.id}
                  onClick={() => openNote(n)}
                  data-testid={`pinned-note-item-${n.id}`}
                  className="block w-full text-left px-2 py-2 border border-transparent hover:border-cyan-sys hover:bg-[rgba(8,247,254,0.06)] font-mono"
                >
                  <div className="font-display tracking-wider text-[12px] truncate text-[var(--txt-primary)]">
                    {n.title || 'Untitled'}
                  </div>
                  <div className="text-[10px] text-[var(--txt-muted)] truncate mt-0.5">
                    {(n.body || '').replace(/\n/g, ' ').slice(0, 60) || '—'}
                  </div>
                  <div className="text-[9px] text-[var(--txt-secondary)] tracking-wider mt-0.5">
                    {cstDateTime(n.updated_at).slice(11)}
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="p-3 flex flex-col h-full">
              <input
                data-testid="pinned-note-title"
                value={title}
                onChange={(e) => { setTitle(e.target.value); setDirty(true); }}
                className="bg-transparent border-b border-[var(--border-muted)] focus:border-cyan-sys outline-none py-1 mb-2 font-display tracking-[0.15em] text-base text-cyan-sys"
              />
              <textarea
                data-testid="pinned-note-body"
                value={body}
                onChange={(e) => { setBody(e.target.value); setDirty(true); }}
                className="flex-1 bg-transparent outline-none font-mono text-[12px] leading-[1.6] resize-none text-[var(--txt-primary)] min-h-[300px]"
              />
              <div className="flex items-center justify-between pt-2 border-t border-[var(--border-default)] mt-2">
                <span className="text-[9px] text-[var(--txt-muted)] tracking-wider">
                  {activeNote ? `updated ${cstDateTime(activeNote.updated_at).slice(11)}` : ''}
                  {dirty && <span className="ml-2 text-hold">● unsaved</span>}
                </span>
                <button
                  data-testid="pinned-note-save"
                  onClick={save}
                  disabled={!dirty}
                  className="text-[10px] tracking-[0.25em] uppercase border border-buy text-buy hover:bg-buy hover:text-black px-2 py-1 disabled:opacity-40"
                >
                  Save
                </button>
              </div>
            </div>
          )}
        </div>
      </aside>
    </>
  );
}
